import { bindable } from 'aurelia-templating';
import { transient } from "aurelia-dependency-injection";
import { observable } from 'aurelia-framework';
import * as Enumerable from "linq";

@transient()
export class Slider {

    private _offset: number;

    public steps: number[] = [];
    public sliderHandleLeft: string;

    @observable()
    public sliderContainer: HTMLElement;

    @observable()
    public sliderHandle: HTMLElement;

    @bindable
    public label: string = "";

    @bindable
    public info: string = "";

    @bindable
    public min: number = 0;

    @bindable
    public step: number = 1;

    @bindable
    public max: number = 100;

    @bindable
    public value: number;

    @bindable
    public tabindex: number;

    @bindable
    isGroupButtons: boolean = false;

    @bindable
    public disabled: boolean = false;

    constructor(private element: Element) { }

    attached() {
        if (this.sliderContainer && this.sliderHandle) {
            setTimeout(() => {
                this.setValue();
            }, 120);
        }
    }

    isGroupButtonsChanged() {
        if (this.isGroupButtons) {
            this.steps = this.getSteps();
        }
    }

    private getSteps(): number[] {
        if (this.step) {
            return Enumerable.range(this.min, (1 + this.max - this.min) / this.step, this.step).toArray();
        }
        return [];
    }

    select(num: number) {
        if (!this.disabled) {
            this.value = num;
        }
    }

    valueChanged() {
        this.setValue();
    }

    maxChanged() {
        if (this.isGroupButtons) {
            this.steps = this.getSteps();
        }
        if (this.value > this.max) {
            this.value = this.max;
        } else {
            this.setValue();
        }
    }

    minChanged() {
        if (this.isGroupButtons) {
            this.steps = this.getSteps();
        }
        if (this.value < this.min) {
            this.value = this.min;
        } else {
            this.setValue();
        }
    }

    sliderHandleChanged(newValue: HTMLElement, oldValue: HTMLElement) {
        if (newValue) {
            newValue.addEventListener('mousedown', this.onMouseDown, false);
            newValue.addEventListener('touchstart', this.onMouseDown, false);
            newValue.addEventListener('keydown', this.onKeyDown, false);
            this.setValue();
        } else if (oldValue) {
            oldValue.removeEventListener('mousedown', this.onMouseDown, false);
            oldValue.removeEventListener('touchstart', this.onMouseDown, false);
            oldValue.removeEventListener('keydown', this.onKeyDown, false);
        }
    }

    onKeyDown = ($event) => {
        if (!this.sliderContainer || !this.sliderContainer.contains($event.target) || this.disabled) {
            return;
        }

        const max = Number(this.max);
        const min = Number(this.min);
        if ($event.keyCode == '37') {
            if (this.value == min) {
                return;
            }
            this.value -= 1;
        }
        else if ($event.keyCode == '39') {
            if (this.value == max) {
                return;
            }
            this.value += 1;
        }
    }

    onMouseDown = ($event) => {
        if (!this.sliderContainer || !this.sliderContainer.contains($event.target) || this.disabled) {
            return;
        }
        const pageX = $event.touches ? $event.touches[0].pageX : $event.pageX;
        document.addEventListener('mousemove', this.onMouseMove, false);
        document.addEventListener('touchmove', this.onMouseMove, false);
        document.addEventListener('mouseup', this.onMouseUp, false);
        document.addEventListener('touchend', this.onMouseUp, false);
        this._offset = pageX - this.sliderHandle.getBoundingClientRect().left;
    }

    onMouseMove = ($event) => {
        const pageX = $event.touches ? $event.touches[0].pageX : $event.pageX;
        const maxLeft = this.sliderContainer.offsetWidth - this.sliderHandle.offsetWidth;
        let left = (pageX - this.sliderContainer.getBoundingClientRect().left - this._offset);
        if (left <= 0) {
            left = 0;
        } else if (left > maxLeft) {
            left = maxLeft;
        }
        const step = 1 / Number(this.step);
        const max = Number(this.max);
        const min = Number(this.min);
        const value = (max - min) * (left / maxLeft) + min;
        this.value = (Math.round(value * step) / step);
    }

    onMouseUp = () => {
        this._offset = null;
        try {
            document.removeEventListener('mousemove', this.onMouseMove, false);
            document.removeEventListener('mouseup', this.onMouseUp, false);
            document.removeEventListener('touchmove', this.onMouseMove, false);
            document.removeEventListener('touchend', this.onMouseUp, false);
        } catch (e) {
            console.error("Error removing event listeners", e);
        }
    }

    onSliderContainerClick = ($event) => {
        if (this.disabled) return;

        const pageX = $event.touches ? $event.touches[0].pageX : $event.pageX;
        const maxLeft = this.sliderContainer.offsetWidth - this.sliderHandle.offsetWidth;
        let left = (pageX - this.sliderContainer.getBoundingClientRect().left - (this.sliderHandle.offsetWidth / 2));
        if (left <= 0) {
            left = 0;
        } else if (left > maxLeft) {
            left = maxLeft;
        }
        const step = 1 / Number(this.step);
        const max = Number(this.max);
        const min = Number(this.min);
        const value = (max - min) * (left / maxLeft) + min;
        this.value = (Math.round(value * step) / step);
    }

    private setValue() {
        if (!this.sliderContainer || !this.sliderHandle) {
            return;
        }
        const percentage = (this.value - this.min) / (this.max - this.min);
        const left = (this.sliderContainer.offsetWidth - this.sliderHandle.offsetWidth) * percentage;

        if (left + this.sliderHandle.offsetWidth <= this.sliderContainer.offsetWidth && left >= 0) {
            this.sliderHandleLeft = left + 'px';
        }
        else if (left <= 0) {
            this.sliderHandleLeft = '0px';
        }
        else if (left + this.sliderHandle.offsetWidth > this.sliderContainer.offsetWidth) {
            this.sliderHandleLeft = (this.sliderContainer.offsetWidth - this.sliderHandle.offsetWidth) + 'px';
        }

        const paramsEvent: any = { detail: { value: this.value }, bubbles: true };
        const changeValueEvent: CustomEvent = new CustomEvent("value-changed", paramsEvent);
        this.element.dispatchEvent(changeValueEvent);
    }

}
